import { apiFetch } from './client';

/**
 * EL INSPECTOR, del lado del panel.
 * Prefijo: /api/inspector
 *
 * El motor lleva construido desde hace tiempo y no tenía pantalla: leía las
 * conversaciones, anotaba lo que encontraba y lo priorizaba, pero el dueño no
 * podía ver nada de eso. Esto es la puerta.
 *
 * Igual que con el menú: **quién decide qué se ve es el servidor**. Acá no se
 * filtra por prioridad ni por negocio — se pide y se dibuja. Filtrar en el
 * navegador sería esconder una fila, y esconder no es aislar.
 */

export type Prioridad = 'ALTA' | 'MEDIA' | 'BAJA' | 'RUIDO' | 'SIN_REVISAR';

export type EstadoHallazgo =
  | 'DETECTADO'
  | 'PROGRAMADO'
  | 'AVISADO'
  | 'RESPONDIDO'
  | 'CUMPLIDO'
  | 'VENCIDO'
  | 'DESCARTADO';

export interface Hallazgo {
  id: string;
  codigo: string;
  tipo: string;
  descripcion: string;
  tema: string | null;
  prioridad: Prioridad;
  /** Por qué el Inspector le puso esa prioridad. Sin esto no se puede discutir. */
  porQue: string | null;
  depuradoAt: string | null;
  fechaCompromiso: string;
  estado: EstadoHallazgo;
  origen: string;
  createdAt: string;
  conversationId: string;
  conQuien: string;
  /** Lo calcula el servidor: dos pestañas no pueden discrepar sobre si venció. */
  vencido: boolean;
}

export interface ResumenInspector {
  alta: number;
  media: number;
  baja: number;
  ruido: number;
  sinRevisar: number;
  vencidos: number;
  /** `null` = nunca depuró. El panel lo dice: lo que se ve no pasó el filtro. */
  ultimaDepuracion: string | null;
}

export interface ObjetivoDisponible {
  id: string;
  nombre: string;
  descripcion: string;
}

export interface CanalDelNegocio {
  id: string;
  name: string;
  status: string;
  /** La promesa que más importa: si está en `true`, marca los chats como vistos. */
  acusarLectura: boolean;
  /** Pedirle a WhatsApp los mensajes viejos al vincular. */
  traerHistorial: boolean;
  simulacro: boolean;
}

/** Cambia cómo lee Athena ese canal. */
export function ajustarCanal(
  id: string,
  cambios: { acusarLectura?: boolean; traerHistorial?: boolean },
): Promise<{ id: string }> {
  return apiFetch(`/api/inspector/canales/${id}`, { method: 'PATCH', body: cambios });
}

export interface ConfigInspector {
  activo: boolean;
  nombre: string;
  horasDeReporte: number[];
  urgencias: { activo: boolean; minutosDeAnticipacion: number };
  canales: string[];
  objetivos: string[];
  queIgnorar: string;
  modelo: string;
  modeloDepuracion: string;
  maxChatsPorCorrida: number;
  soloChatsActivosDias: number;
  verImagenes: boolean;
  transcribirAudios: boolean;
}

export interface RespuestaConfig {
  config: ConfigInspector;
  objetivosDisponibles: ObjetivoDisponible[];
  canales: CanalDelNegocio[];
}

export interface FiltroHallazgos {
  estado?: string;
  prioridad?: Prioridad;
  incluirRuido?: boolean;
  limite?: number;
}

export function getHallazgos(
  filtro: FiltroHallazgos = {},
): Promise<{ hallazgos: Hallazgo[]; total: number }> {
  return apiFetch('/api/inspector/hallazgos', { query: filtro });
}

export function getResumenInspector(): Promise<ResumenInspector> {
  return apiFetch('/api/inspector/resumen');
}

export function cambiarEstadoHallazgo(
  id: string,
  estado: 'CUMPLIDO' | 'DESCARTADO' | 'PROGRAMADO',
): Promise<{ id: string; estado: string }> {
  return apiFetch(`/api/inspector/hallazgos/${id}`, { method: 'PATCH', body: { estado } });
}

export function getConfigInspector(): Promise<RespuestaConfig> {
  return apiFetch('/api/inspector/config');
}

export function guardarConfigInspector(
  cambios: Partial<ConfigInspector>,
): Promise<{ config: ConfigInspector }> {
  return apiFetch('/api/inspector/config', { method: 'PUT', body: cambios });
}

// ═══════════════════════════════════════════════════════════════════════════
// LA AGENDA Y LAS SEÑALES
// ═══════════════════════════════════════════════════════════════════════════

export interface ItemDeAgenda {
  id: string;
  codigo: string;
  que: string;
  conQuien: string;
  cuando: string;
  prioridad: Prioridad;
  porQue: string | null;
  vencido: boolean;
}

export interface Agenda {
  vencidos: ItemDeAgenda[];
  hoy: ItemDeAgenda[];
  manana: ItemDeAgenda[];
  estaSemana: ItemDeAgenda[];
  masAdelante: ItemDeAgenda[];
}

export type TipoDeSenal = 'esperando' | 'enfriado' | 'acumulado' | 'repetido';

export interface Senal {
  tipo: TipoDeSenal;
  titulo: string;
  /** Por qué importa. Se muestra siempre: una alerta sin razón es ruido. */
  detalle: string;
  conversationId: string | null;
  peso: number;
}

export function getAgenda(): Promise<Agenda> {
  return apiFetch('/api/inspector/agenda');
}

/** `noPudeVer` viaja a propósito: un tablero que muestra cero sin aclarar
 *  que no pudo mirar, miente. */
export function getSenales(): Promise<{ senales: Senal[]; noPudeVer: string[] }> {
  return apiFetch('/api/inspector/senales');
}

// ═══ EL TABLERO DE SALUD ═══ (ver `salud.service.ts` en la API)

export type TipoDeChat = 'todos' | 'personas' | 'grupos';
export type EstadoRelacion = 'activa' | 'enfriandose' | 'fria';

export interface ChatEnCubeta {
  conversationId: string;
  quien: string;
  mensajes: number;
}

export interface Relacion {
  conversationId: string;
  quien: string;
  esGrupo: boolean;
  mensajes: number;
  tuyos: number;
  ultimoContacto: string;
  diasSinHablar: number;
  ritmoDias: number | null;
  estado: EstadoRelacion | null;
  teEspera: boolean;
  esperaDesde: string | null;
  minutosRespuesta: number | null;
  serie: number[];
}

export interface Salud {
  dias: number;
  desde: string;
  hasta: string;
  totales: {
    mensajes: number;
    tuyos: number;
    deOtros: number;
    chatsActivos: number;
    teEsperan: number;
    seEnfrian: number;
    minutosMedianosRespuesta: number | null;
  };
  porDia: Array<{ dia: string; tuyos: number; deOtros: number; chats: ChatEnCubeta[] }>;
  porHora: Array<{ hora: number; tuyos: number; chats: ChatEnCubeta[] }>;
  porFrente: Array<{ frenteId: string | null; nombre: string; color: string | null; mensajes: number; chats: ChatEnCubeta[] }>;
  relaciones: Relacion[];
  frentes: Array<{ id: string; nombre: string; color: string }>;
}

export function getSalud(filtro: { dias: number; tipo: TipoDeChat; frenteId?: string }): Promise<Salud> {
  return apiFetch('/api/inspector/salud', { query: filtro });
}

// ═══ "¿QUIÉN ES?" ═══ (ver `quien-es.service.ts` en la API)

export type RelacionDePersona = 'cliente' | 'equipo' | 'familia' | 'proveedor' | 'amigo' | 'otro';

export interface PersonaPorConocer {
  contactId: string;
  conversationId: string;
  comoSeVe: string;
  sugerencia: string | null;
  porQue: string;
}

export function getPorConocer(): Promise<{ personas: PersonaPorConocer[]; respondidasHoy: number; cupo: number }> {
  return apiFetch('/api/inspector/por-conocer');
}

export function identificarPersona(
  contactId: string,
  r: { nombre?: string; relacion?: RelacionDePersona; cadenciaDias?: number | null; sinSeguimiento?: boolean },
): Promise<{ listo: boolean }> {
  return apiFetch(`/api/inspector/personas/${contactId}/quien`, { method: 'POST', body: r });
}
